import React, { createContext, useCallback, useContext, useState } from 'react';
import { PlannerContext } from '@modules/planner/planner-context';
import { WorkOrder } from '@models';

export type BulkSelectedTask = {
  id: string;
  isMultiTask: boolean;
  multitaskWoIds?: string[];
  workOrderId?: WorkOrder['workOrderId'];
  multitaskWorkOrderIds?: Array<WorkOrder['workOrderId']>;
  unitId: string;
};

export type BulkOperationDialogContextType = {
  selected: BulkSelectedTask[];
  addToSelection: (id: BulkSelectedTask) => void;
  addManyToSelection: (tasks: BulkSelectedTask[]) => void;
  replaceSelection: (tasks: BulkSelectedTask[]) => void;
  removeFromSelection: (id: string) => void;
  clearSelection: () => void;
  bulkEditModeOn: boolean;
  setBulkEditModeOn: (value: boolean) => void;
  getSelectedWorkOrders: (unitId?: string) => WorkOrder[];
};

export const BulkOperationsContext =
  createContext<BulkOperationDialogContextType>({
    selected: [],
    bulkEditModeOn: false,
    addToSelection: () => {},
    addManyToSelection: () => {},
    replaceSelection: () => {},
    removeFromSelection: () => {},
    clearSelection: () => {},
    setBulkEditModeOn: () => {},
    getSelectedWorkOrders: () => [],
  });

export const BulkOperationsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selected, setSelected] = useState<BulkSelectedTask[]>([]);
  const [bulkEditModeOn, setBulkEditModeOn] = useState<boolean>(false);
  // TODO: FIXME:
  // skontrolovat ci tato smakolada s contextom a getSelectedWorkOrders nesposobuje nejaky brutalny rerendering
  const { calendarSource } = useContext(PlannerContext);

  const addToSelection = useCallback((task: BulkSelectedTask) => {
    setSelected((prevSelected) => {
      if (prevSelected.some((item) => item.id === task.id)) {
        return prevSelected;
      }

      return [...prevSelected, task];
    });
  }, []);

  const addManyToSelection = useCallback((tasks: BulkSelectedTask[]) => {
    setSelected((prevSelected) => {
      const selectedById = new Map<string, BulkSelectedTask>();

      prevSelected.forEach((item) => {
        selectedById.set(item.id, item);
      });

      tasks.forEach((task) => {
        selectedById.set(task.id, task);
      });

      return Array.from(selectedById.values());
    });
  }, []);

  const replaceSelection = useCallback((tasks: BulkSelectedTask[]) => {
    setSelected(tasks);
  }, []);

  const removeFromSelection = useCallback(
    (id: string) => {
      setSelected(selected.filter((item) => item.id !== id));
    },
    [selected]
  );

  const clearSelection = useCallback(() => {
    setSelected([]);
  }, []);

  const getSelectedWorkOrders = useCallback(
    (unitId?: string) => {
      if (!unitId) return [];
      const unitWos =
        calendarSource?.yAxis?.data?.find((unit) => unit.id === unitId)
          ?.workOrders ?? [];
      if (unitWos.length === 0) {
        return [];
      }
      return unitWos.filter((wo) => selected.some((s) => s.id === wo.id));
    },
    [calendarSource?.yAxis?.data, selected]
  );

  return (
    <BulkOperationsContext.Provider
      value={{
        addToSelection,
        addManyToSelection,
        replaceSelection,
        removeFromSelection,
        clearSelection,
        selected,
        bulkEditModeOn,
        setBulkEditModeOn,
        getSelectedWorkOrders,
      }}
    >
      {children}
    </BulkOperationsContext.Provider>
  );
};
